import {
  FormControlLabel,
  Radio,
  RadioGroup,
  useTheme,
  TextField,
  FormControl,
  Autocomplete,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';

import { Icon } from '@verantos-dev/shared-components';

import { useAttributeInput } from './useAttributeInput';
import { ATTR_VAL_NO, ATTR_VAL_YES } from '../../constants';

const RadioInput = ({ value, options = [], disabled, onChange }: any) => {
  const theme = useTheme();

  return (
    <RadioGroup
      row
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      sx={{ display: 'flex', gap: theme.spacing(4) }}
    >
      {options.map((option: string) => (
        <FormControlLabel
          key={option}
          value={option}
          control={<Radio size="small" disabled={disabled} />}
          label={option}
        />
      ))}
    </RadioGroup>
  );
};

const BooleanRadio = ({ value, disabled, onChange }: any) => {
  return (
    <RadioInput
      value={value === true ? ATTR_VAL_YES : value === false ? ATTR_VAL_NO : ''}
      disabled={disabled}
      options={[ATTR_VAL_YES, ATTR_VAL_NO]}
      onChange={(val: string) => onChange(val === ATTR_VAL_YES)}
    />
  );
};

const NumberField = ({
  id,
  value,
  disabled,
  width = 200,
  placeholder,
  min,
  max,
  integerOnly,
  onChange,
}: any) => (
  <TextField
    type="number"
    size="small"
    value={value ?? ''}
    disabled={disabled}
    placeholder={placeholder}
    onWheel={(e) => (e.target as HTMLElement).blur()}
    onKeyDown={(e) => {
      const blockedKeys = integerOnly ? ['e', 'E', '+', '.'] : ['e', 'E', '+'];
      if (blockedKeys.includes(e.key)) {
        e.preventDefault();
      }
    }}
    sx={{ width }}
    onChange={(e) => {
      const val = e.target.value;

      if (val === '') {
        onChange(undefined);
        return;
      }

      const num = Number(val);

      if (integerOnly && !Number.isInteger(num)) return;
      if (min !== undefined && num < min) return;
      if (max !== undefined && num > max) return;

      onChange(num);
    }}
    slotProps={{
      htmlInput: {
        min,
        max,
      },
      input: {
        endAdornment: value !== undefined && value !== null && !disabled && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              aria-label="clear"
              onClick={() => onChange(undefined)}
            >
              <Icon id={`${id}-clear-icon`} icon="clear" width={16} />
            </IconButton>
          </InputAdornment>
        ),
      },
    }}
  />
);

export const AttributeInput = ({ attribute, attributeRule, isDisabled, onChange }: any) => {
  const theme = useTheme();
  const { handleChange, getInputConfig } = useAttributeInput(attribute, attributeRule, onChange);

  const config = getInputConfig();

  switch (config.type) {
    case 'boolean-radio':
      return <BooleanRadio value={attribute.value} disabled={isDisabled} onChange={handleChange} />;

    case 'radio':
      return (
        <RadioInput
          value={attribute.value}
          options={config.radioOptions || []}
          disabled={isDisabled}
          onChange={handleChange}
        />
      );

    case 'list':
      return (
        <FormControl size="small" sx={{ minWidth: 200, my: theme.spacing(3), width: '100%' }}>
          <Autocomplete
            disabled={isDisabled}
            options={config.autocompleteOptions || []}
            value={(attribute.value as string) || null}
            onChange={(_, newValue) =>
              onChange(attribute.name, {
                value: newValue,
                isUnknown: false,
              })
            }
            renderInput={(params) => (
              <TextField {...params} label={config.attributeName} size="small" />
            )}
          />
        </FormControl>
      );

    case 'number':
      return <NumberField disabled={isDisabled} {...config.numberFieldProps} />;

    case 'year':
      return (
        <DatePicker
          views={['year']}
          openTo="year"
          value={
            attribute.value
              ? dayjs()
                  .year(attribute.value as number)
                  .startOf('year')
              : null
          }
          minDate={dayjs().year(1000).startOf('year')}
          maxDate={dayjs()}
          disabled={isDisabled}
          onChange={(newValue, context) => {
            if (context?.validationError) {
              handleChange(undefined);
              return;
            }
            handleChange(newValue ? newValue.year() : undefined);
          }}
          slotProps={{
            textField: {
              size: 'small',
              placeholder: 'YYYY',
              sx: { width: 150 },
            },
            field: {
              clearable: true,
              onClear: () => handleChange(undefined),
            },
          }}
        />
      );

    case 'month':
      return <NumberField disabled={isDisabled} {...config.numberFieldProps} />;

    case 'day':
      return <NumberField disabled={isDisabled} {...config.numberFieldProps} />;

    case 'text':
      return (
        <TextField
          type="text"
          size="small"
          value={(attribute.value as string) ?? ''}
          disabled={isDisabled}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
          sx={{ width: 200 }}
          onChange={(e) => handleChange(e.target.value || undefined)}
          slotProps={{
            input: {
              endAdornment: attribute.value && !isDisabled && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="clear"
                    onClick={() => handleChange(undefined)}
                  >
                    <Icon id={`${attribute.name}-text-clear-icon`} icon="clear" width={16} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      );

    case 'time':
      return (
        <TimePicker
          value={attribute.value ? dayjs(attribute.value, 'HH:mm') : null}
          onChange={(newValue, context) => {
            if (context?.validationError) {
              handleChange(undefined);
              return;
            }
            const formatted = newValue ? newValue.format('HH:mm') : undefined;
            handleChange(formatted);
          }}
          views={['hours', 'minutes']}
          timeSteps={{ minutes: 1 }}
          minutesStep={1}
          closeOnSelect={false}
          localeText={{ fieldMeridiemPlaceholder: () => 'AM/PM' }}
          disabled={isDisabled}
          slotProps={{
            textField: {
              size: 'small',
              placeholder: 'hh:mm AM/PM',
              sx: { width: 210 },
            },
            field: {
              clearable: true,
              onClear: () => handleChange(undefined),
            },
            actionBar: {
              actions: ['cancel', 'accept'],
            },
          }}
        />
      );

    case 'null':
    default:
      return null;
  }
};
